import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import classNames from "classnames"

import {
  useAllocateEnterpriseContractorTokenMutation,
  useDeallocateEnterpriseContractorTokenMutation,
} from "store/api/enterpriseApi"
import { StorageKeys } from "types/enums/StorageKeys"

import { getStorageItem, setStorageItem } from "utils/localStorage"
import { useContractors } from "hooks/useContractors"
import { useLogin } from "hooks/useLogin"
import { useUserProfile } from "hooks/useUserProfile"

import styles from "./index.module.scss"

export const AccountSwitcher = () => {
  const { userProfile, isEnterprise, enterpriseContractorIds } = useUserProfile()
  const navigate = useNavigate()
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [selectedContractorId, setSelectedContractorId] = useState<number | null>(null)
  const [isDropdownOpen, setDropdownOpen] = useState(false)
  const [allocateEnterpriseContractorToken] = useAllocateEnterpriseContractorTokenMutation()
  const [deallocateEnterpriseContractorToken] = useDeallocateEnterpriseContractorTokenMutation()

  const { contractors: fetchedContractors } = useContractors(enterpriseContractorIds)

  const { contractors, selectedContractor, selectedAccountLabel } = useMemo(() => {
    const cachedContractors = getStorageItem(StorageKeys.EnterpriseContractorsMenuDropdown)
    const contractors =
      fetchedContractors.length > 0
        ? fetchedContractors.map(contractor => ({ id: contractor.id, name: contractor.name }))
        : (cachedContractors ?? [])
    const selectedContractor = contractors.find(contractor => contractor.id === selectedContractorId)
    const selectedAccountLabel = selectedContractor?.name || "Master Account"

    return { contractors, selectedContractor, selectedAccountLabel }
  }, [fetchedContractors, selectedContractorId])

  const { login, getCurrentAuthToken, getEnterpriseMasterAccountToken } = useLogin()

  const showsAccountSwitcher = isEnterprise || getEnterpriseMasterAccountToken()

  const deallocateContractorToken = async (contractorId: number | null) => {
    const authToken = getEnterpriseMasterAccountToken()
    const currentToken = getCurrentAuthToken()
    const tokenId = Number(currentToken?.split("|")?.[0] ?? "0")

    if (!contractorId || !tokenId || !authToken) return

    try {
      await deallocateEnterpriseContractorToken({ contractorId, tokenId, authToken }).unwrap()
    } catch (error) {
      console.error("Failed to deallocate contractor token", error)
    }
  }

  const handleAccountClick = async (contractorId: number | null) => {
    if (!isEnterprise && !contractorId) {
      await deallocateContractorToken(selectedContractorId)
      await login({ accessToken: getEnterpriseMasterAccountToken(), masterToken: null })
      setSelectedContractorId(null)
      setDropdownOpen(false)
      navigate("/dashboard", { replace: true })
      return
    }

    if (contractorId === selectedContractorId) return

    if (contractorId) {
      const masterAccountToken = isEnterprise ? getCurrentAuthToken() : getEnterpriseMasterAccountToken()
      const tokenAllocation = await allocateEnterpriseContractorToken({
        contractorId,
        authToken: masterAccountToken,
      }).unwrap()
      if (!isEnterprise) {
        await deallocateContractorToken(selectedContractorId)
      }
      await login({ accessToken: tokenAllocation.data.token, masterToken: masterAccountToken })
      setSelectedContractorId(contractorId)
      setDropdownOpen(false)
      return
    }
  }

  useEffect(() => {
    const currentContractorId = userProfile?.contractor?.id
    if (currentContractorId) {
      setSelectedContractorId(currentContractorId)
    } else if (isEnterprise) {
      setSelectedContractorId(null)
    }
  }, [isEnterprise, userProfile])

  useEffect(() => {
    if (isEnterprise && contractors) {
      setStorageItem(StorageKeys.EnterpriseContractorsMenuDropdown, contractors)
    }
  }, [isEnterprise, contractors])

  useEffect(() => {
    if (!showsAccountSwitcher) {
      return
    }

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setDropdownOpen(false)
      }
    }

    document.addEventListener("mousedown", closeOnOutsideClick)

    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick)
    }
  }, [showsAccountSwitcher])

  if (!showsAccountSwitcher) return null

  const isMasterAccountSelected = !selectedContractor && isEnterprise

  return (
    <div ref={wrapperRef} className={classNames(styles.accountSwitcher)}>
      <button
        className={classNames(styles.trigger, { [styles.triggerActive]: isDropdownOpen })}
        type="button"
        onClick={() => setDropdownOpen(open => !open)}
        aria-expanded={isDropdownOpen}
        aria-haspopup="menu"
        aria-label="Switch account"
      >
        <span>{selectedAccountLabel}</span>
        <span className={classNames(styles.arrow, { [styles.arrowOpen]: isDropdownOpen })} aria-hidden="true" />
      </button>
      <div className={classNames(styles.dropdown, { [styles.dropdownOpen]: isDropdownOpen })} role="menu">
        <div className={styles.dropdownCard}>
          <button
            className={classNames(styles.dropdownItem, {
              [styles.dropdownItemSelected]: isMasterAccountSelected,
              [styles.dropdownItemSelectable]: !isMasterAccountSelected,
            })}
            type="button"
            role="menuitemradio"
            aria-checked={isMasterAccountSelected}
            onClick={() => {
              if (!isMasterAccountSelected) {
                handleAccountClick(null)
              }
            }}
          >
            <span>Master Account</span>
            {isMasterAccountSelected && <span className={styles.checkmark} aria-hidden="true" />}
          </button>
          {contractors.length > 0 ? (
            <>
              {contractors.map(contractor => {
                const isSelected = contractor.id === selectedContractorId
                const handleClick = () => handleAccountClick(contractor.id)

                return (
                  <button
                    key={contractor.id}
                    className={classNames(styles.dropdownItem, {
                      [styles.dropdownItemSelected]: isSelected,
                      [styles.dropdownItemSelectable]: !isSelected,
                    })}
                    type="button"
                    role="menuitemradio"
                    aria-checked={isSelected}
                    onClick={handleClick}
                  >
                    <span>{contractor.name}</span>
                    {isSelected && <span className={styles.checkmark} aria-hidden="true" />}
                  </button>
                )
              })}
            </>
          ) : (
            <div className={styles.emptyState} role="menuitem">
              No contractors found.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
